import React, { useEffect, useState } from "react";
import {
  Card,
  Tabs,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Typography,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  UserAddOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { expertInviteService } from "../Services/ExpertInviteService";
import {
  InvitationStatusEnum,
  InvitationStatusLabels,
  InvitationStatusColors,
} from "Common/Constants/Enums/InvitationEnums";

const { Title } = Typography;

const formatDate = (d) => (d ? dayjs(d).format("DD MMM YYYY, HH:mm") : "-");

const ExpertPersons = () => {
  const [activeTab, setActiveTab] = useState("registered");
  const [experts, setExperts] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Track which row AND which action is running so only the clicked button spins.
  const [action, setAction] = useState({ id: null, type: null });
  const [form] = Form.useForm();

  const fetchExperts = async () => {
    try {
      setLoadingExperts(true);
      const data = await expertInviteService.getRegisteredExperts();
      setExperts(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      // interceptor shows the error toast
    } finally {
      setLoadingExperts(false);
    }
  };

  const fetchInvites = async () => {
    try {
      setLoadingInvites(true);
      const data = await expertInviteService.getInvites();
      setInvites(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      // interceptor shows the error toast
    } finally {
      setLoadingInvites(false);
    }
  };

  useEffect(() => {
    fetchExperts();
    fetchInvites();
  }, []);

  const handleSendInvite = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await expertInviteService.sendInvite(values);
      toast.success("Expert invitation sent");
      setInviteOpen(false);
      form.resetFields();
      setActiveTab("invitations");
      fetchInvites();
    } catch (err) {
      if (err?.errorFields) return; // form validation errors
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id) => {
    try {
      setAction({ id, type: "resend" });
      await expertInviteService.resendInvite(id);
      toast.success("Invitation resent");
      fetchInvites();
    } catch (err) {
      // interceptor shows the error toast (e.g. cooldown)
    } finally {
      setAction({ id: null, type: null });
    }
  };

  const handleRevoke = async (id) => {
    try {
      setAction({ id, type: "revoke" });
      await expertInviteService.revokeInvite(id);
      toast.success("Invitation revoked");
      fetchInvites();
    } catch (err) {
      // interceptor shows the error toast
    } finally {
      setAction({ id: null, type: null });
    }
  };

  const expertColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Expert Code", dataIndex: "expertCode", key: "expertCode", render: (v) => v || "-" },
    {
      title: "Profile",
      dataIndex: "isProfileDetailCompleted",
      key: "profile",
      render: (done) => (
        <Tag color={done ? "green" : "orange"}>{done ? "Completed" : "Incomplete"}</Tag>
      ),
    },
    {
      title: "Experience",
      dataIndex: "yearsOfExperience",
      key: "yearsOfExperience",
      render: (v) => `${v ?? 0} yr`,
    },
    {
      title: "Services",
      dataIndex: "expertServiceCount",
      key: "expertServiceCount",
      render: (v) => v ?? 0,
    },
  ];

  const inviteColumns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || "-",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={InvitationStatusColors[s] || "default"}>
          {InvitationStatusLabels[s] || "Unknown"}
        </Tag>
      ),
    },
    { title: "Invited On", dataIndex: "inviteSentAt", key: "inviteSentAt", render: formatDate },
    { title: "Expires At", dataIndex: "expiresAt", key: "expiresAt", render: formatDate },
    { title: "Sends", dataIndex: "sendCount", key: "sendCount", render: (v) => v ?? 1 },
    {
      title: "Action",
      key: "action",
      render: (_, r) => {
        if (r.status !== InvitationStatusEnum.Pending) {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
        // Resend has a one-hour cooldown from inviteSentAt. Disable it and show
        // the remaining time so we do not fire an invalid call to the server.
        const sentAt = r.inviteSentAt ? new Date(r.inviteSentAt) : null;
        const diffInMinutes = sentAt ? Math.floor((new Date() - sentAt) / (1000 * 60)) : Infinity;
        const canResend = diffInMinutes >= 60;
        const remainingMinutes = 60 - diffInMinutes;
        const resendLoading = action.id === r.id && action.type === "resend";
        return (
          <div>
            <Space>
              <Button
                type="link"
                size="small"
                icon={<SendOutlined />}
                disabled={!canResend || resendLoading}
                loading={resendLoading}
                onClick={() => handleResend(r.id)}
              >
                Resend
              </Button>
              <Popconfirm
                title="Revoke this invitation?"
                okText="Revoke"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleRevoke(r.id)}
              >
                <Button type="link" size="small" danger icon={<StopOutlined />} loading={action.id === r.id && action.type === "revoke"}>
                  Revoke
                </Button>
              </Popconfirm>
            </Space>
            {!canResend && (
              <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                You can resend after {remainingMinutes} minute{remainingMinutes > 1 ? "s" : ""}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const items = [
    {
      key: "registered",
      label: "Registered Experts",
      children: (
        <Table
          columns={expertColumns}
          dataSource={experts}
          rowKey="id"
          loading={loadingExperts}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      ),
    },
    {
      key: "invitations",
      label: "Invitations",
      children: (
        <Table
          columns={inviteColumns}
          dataSource={invites}
          rowKey="id"
          loading={loadingInvites}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      ),
    },
  ];

  return (
    <Card
      className="card-professional"
      title={
        <Space>
          <UserAddOutlined style={{ color: "#9A6B0E" }} />
          <Title level={4} style={{ margin: 0 }}>
            Expert Persons
          </Title>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchExperts();
              fetchInvites();
            }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: "#9A6B0E", borderColor: "#9A6B0E" }}
            onClick={() => setInviteOpen(true)}
          >
            Invite Expert
          </Button>
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />

      <Modal
        title="Invite Expert"
        open={inviteOpen}
        onOk={handleSendInvite}
        onCancel={() => {
          setInviteOpen(false);
          form.resetFields();
        }}
        confirmLoading={submitting}
        okText="Send Invite"
        okButtonProps={{ style: { background: "#9A6B0E", borderColor: "#9A6B0E" } }}
      >
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter an email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="expert@example.com" />
          </Form.Item>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: "Please enter the first name" }]}
          >
            <Input placeholder="First name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: "Please enter the last name" }]}
          >
            <Input placeholder="Last name" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ExpertPersons;
